import type { Feature, Sheet, SheetData } from 'write-excel-file/browser';
import { formatCampaignWeekdays, parseCampaignWeekdays } from './campaignWeekdays';
import {
    findElement,
    getChildElements,
    getSelfClosingTagMarkup,
    replaceElement,
} from 'write-excel-file/utility';

export interface CampaignImport {
    name: string;
    status?: 0 | 1;
    start_date: string;
    end_date: string;
    books: Array<{ isbn: string; title: string }>;
    settings: Record<string, unknown>;
    targets: Array<{ type: 'user_guid' | 'crm_guid'; identifier: string }>;
}

export interface CampaignWorkbookRecord {
    id: string;
    guid: string;
    name: string;
    status: 0 | 1;
    start_date: string;
    end_date: string;
    public: {
        books: CampaignImport['books'];
        settings: Record<string, unknown> & {
            csv_email?: string | null;
            csv_schedule?: { weekdays: number[]; time: string } | null;
        };
    };
    targets: Array<{ user_guid: string }>;
}

const textCell = (value = '') => ({ value, type: String, format: '@' });

const textInputColumns: Feature<File | Blob | ArrayBuffer> = {
    files: {
        transform: {
            'xl/worksheets/sheet{id}.xml': {
                transform: (xml, { sheet }) => {
                    if (!['Kirjat', 'CRM-kohteet', 'Nova-kohteet'].includes(sheet || '')) return xml;
                    const headerCell = findElement(xml, 'c');
                    const columns = findElement(xml, 'cols');
                    const textStyle = headerCell?.openingTagAttributes.s;
                    if (textStyle === undefined || !columns) {
                        throw new Error('Excel-pohjan tekstisarakkeiden muotoilu epäonnistui.');
                    }
                    // Reuse the text header's style as the default for every row in each input column.
                    for (const column of getChildElements(xml, columns).reverse()) {
                        xml = replaceElement(xml, column, getSelfClosingTagMarkup('col', {
                            ...column.openingTagAttributes,
                            style: textStyle,
                        }));
                    }
                    return xml;
                },
            },
        },
    },
};

const campaignFields = [
    { key: 'name', label: 'Kampanjan nimi' },
    { key: 'status', label: 'Tila (0 = aktiivinen, 1 = passivoitu)' },
    { key: 'start_date', label: 'Alkaa' },
    { key: 'end_date', label: 'Päättyy' },
    { key: 'csv_email', label: 'CSV-viennin vastaanottaja' },
    { key: 'csv_weekdays', label: 'Vientipäivät (ma,ti,ke,to,pe,la,su)' },
    { key: 'csv_time', label: 'Vientiaika (Suomen aika, HH:mm)' },
    { key: 'settings_json', label: 'Muut kampanja-asetukset (JSON)' },
] as const;

const helsinkiDateParts = (date: Date) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Helsinki',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);
    const values = Object.fromEntries(parts.map(({ type, value }) => [type, Number(value)]));
    return {
        year: values.year,
        month: values.month,
        day: values.day,
    };
};

const toHelsinkiIso = (
    year: number,
    month: number,
    day: number,
    time = '00:00:00'
) => {
    const offset = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Helsinki',
        timeZoneName: 'longOffset',
    }).formatToParts(new Date(Date.UTC(year, month - 1, day, 12)))
        .find(part => part.type === 'timeZoneName')?.value;
    const match = offset && /^GMT([+-])(\d{2}):(\d{2})$/.exec(offset);
    if (!match) throw new Error('Suomen aikavyöhykkeen selvittäminen epäonnistui.');
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${time}${match[1]}${match[2]}:${match[3]}`;
};

export const getDefaultCampaignDates = (now = new Date()) => {
    const { year, month, day } = helsinkiDateParts(now);
    const nextMonth = new Date(Date.UTC(year, month, 1));
    const endDay = Math.min(day, new Date(Date.UTC(year, month + 1, 0)).getUTCDate());
    return {
        startDate: toHelsinkiIso(year, month, day),
        endDate: toHelsinkiIso(
            nextMonth.getUTCFullYear(),
            nextMonth.getUTCMonth() + 1,
            endDay,
            '23:59:59'
        ),
    };
};

const downloadWorkbook = async (
    campaign?: CampaignWorkbookRecord
) => {
    const { default: writeExcelFile } = await import(
        'write-excel-file/browser'
    );
    const { startDate, endDate } = getDefaultCampaignDates();
    const settings = campaign?.public.settings || {};
    const schedule = settings.csv_schedule;
    const defaults: Record<string, string> = campaign ? {
        name: campaign.name,
        status: String(campaign.status),
        start_date: campaign.start_date,
        end_date: campaign.end_date,
        csv_email: settings.csv_email || '',
        csv_weekdays: schedule ? formatCampaignWeekdays(schedule.weekdays) : '',
        csv_time: schedule?.time || '',
        settings_json: JSON.stringify(Object.fromEntries(
            Object.entries(settings).filter(([key]) =>
                !['csv_email', 'csv_schedule'].includes(key)
            )
        ), null, 2),
    } : {
        status: '0',
        start_date: startDate,
        end_date: endDate,
        settings_json: '{}',
    };
    const sheets: Sheet<Blob>[] = [
        {
            sheet: 'Kampanja',
            data: [
                [textCell('Kenttä'), textCell('Arvo')],
                ...campaignFields.map(({ key, label }) => [
                    textCell(label),
                    textCell(defaults[key] || ''),
                ]),
            ] as SheetData,
            columns: [{ width: 38 }, { width: 55 }],
        },
        {
            sheet: 'Kirjat',
            data: [
                [textCell('ISBN'), textCell('Kirjan nimi')],
                ...(campaign
                    ? campaign.public.books.map(book => [
                        textCell(book.isbn), textCell(book.title),
                    ])
                    : [[textCell(), textCell()]]),
            ] as SheetData,
            columns: [{ width: 24 }, { width: 50 }],
        },
        {
            sheet: 'CRM-kohteet',
            data: [
                [textCell('CRM GUID')],
                [textCell()],
            ] as SheetData,
            columns: [{ width: 50 }],
        },
        {
            sheet: 'Nova-kohteet',
            data: [
                [textCell('Nova-käyttäjän GUID')],
                ...(campaign
                    ? campaign.targets.map(target => [textCell(target.user_guid)])
                    : [[textCell()]]),
            ] as SheetData,
            columns: [{ width: 50 }],
        },
    ];
    await writeExcelFile(sheets, { features: [textInputColumns] })
        .toFile(campaign
            ? `sample-books-campaign-${campaign.guid}.xlsx`
            : 'sample-books-campaign-template.xlsx');
};

export const downloadCampaignTemplate = () => downloadWorkbook();

export const downloadCampaignWorkbook = (campaign: CampaignWorkbookRecord) =>
    downloadWorkbook(campaign);

const toText = (value: unknown) => {
    if (value == null) return '';
    if (value instanceof Date) return value.toISOString();
    return String(value).trim();
};

const toTime = (value: unknown) => {
    if (value instanceof Date) {
        if (!Number.isFinite(value.getTime())) {
            throw new Error('csv_time: käytä HH:mm-muotoa.');
        }
        return value.toISOString().slice(11, 16);
    }
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value < 1) {
        const minutes = Math.round(value * 24 * 60);
        if (minutes < 1440 && Math.abs(value * 24 * 60 - minutes) < 0.000001) {
            const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
            return `${hours}:${String(minutes % 60).padStart(2, '0')}`;
        }
    }
    return toText(value);
};

const parseDate = (value: unknown, field: string) => {
    const text = toText(value);
    const date = new Date(text);
    if (!text || !Number.isFinite(date.getTime())) {
        throw new Error(`${field}: anna kelvollinen ISO-päivämäärä ja kellonaika.`);
    }
    return date.toISOString();
};

const rowsToObjects = (rows: unknown[][]) => {
    const [header = [], ...data] = rows;
    const keys = header.map(toText);
    return data
        .filter(row => row.some(value => toText(value) !== ''))
        .map(row =>
            Object.fromEntries(
                keys.map((key, index) => [key, row[index]])
            )
        );
};

const campaignFieldKeys = new Map<string, string>(
    campaignFields.flatMap(({ key, label }) => [[key, key], [label, key]])
);
campaignFieldKeys.set('Vientipäivät (0–6, pilkuilla)', 'csv_weekdays');

const resolveSheet = (sheetNames: string[], current: string, legacy: string) =>
        sheetNames.includes(current) ? current : sheetNames.includes(legacy) ? legacy : null;

export const parseCampaignWorkbook = async (
    file: File
): Promise<CampaignImport> => {
    const { default: readXlsxFile, readSheetNames } = await import(
        'read-excel-file'
    );
    const sheetNames = await readSheetNames(file);
    const campaignSheet = resolveSheet(sheetNames, 'Kampanja', 'campaign');
    const booksSheet = resolveSheet(sheetNames, 'Kirjat', 'books');
    if (!campaignSheet || !booksSheet) {
        throw new Error('Työkirjasta puuttuu Kampanja- tai Kirjat-välilehti.');
    }
    const targetSheets: Array<{
        sheet: string;
        type: 'crm_guid' | 'user_guid';
        headers: string[];
    }> = [];
    for (const target of [
        { current: 'CRM-kohteet', legacy: 'crm targets', type: 'crm_guid' as const, header: 'CRM GUID' },
        { current: 'Nova-kohteet', legacy: 'nova targets', type: 'user_guid' as const, header: 'Nova-käyttäjän GUID' },
    ]) {
        const sheet = resolveSheet(sheetNames, target.current, target.legacy);
        if (sheet) targetSheets.push({ sheet, type: target.type, headers: [target.header, 'identifier'] });
    }
    const hasLegacyTargets = sheetNames.includes('targets');
    if (!targetSheets.length && !hasLegacyTargets) {
        throw new Error('Lisää crm targets tai nova targets -välilehti.');
    }
    if (targetSheets.length && hasLegacyTargets) {
        throw new Error('Käytä joko uusia kohdevälilehtiä tai vanhaa targets-välilehteä, ei molempia.');
    }
    const [campaignRows, bookRows, targets] = await Promise.all([
        readXlsxFile(file, { sheet: campaignSheet }),
        readXlsxFile(file, { sheet: booksSheet }),
        targetSheets.length
            ? Promise.all(targetSheets.map(async ({ sheet, type, headers }) => {
                const rows = await readXlsxFile(file, { sheet });
                if (!headers.includes(toText(rows[0]?.[0]))) {
                    throw new Error(`${sheet}: ensimmäisen sarakkeen otsikko ei ole kelvollinen.`);
                }
                return rows.slice(1)
                    .filter(row => toText(row[0]))
                    .map(row => ({ type, identifier: toText(row[0]) }));
            })).then(groups => groups.flat())
            : readXlsxFile(file, { sheet: 'targets' }).then(rows =>
                rowsToObjects(rows).map((row): CampaignImport['targets'][number] => {
                    const type = toText(row.type);
                    if (type !== 'user_guid' && type !== 'crm_guid') {
                        throw new Error('Kohteen type-arvon tulee olla user_guid tai crm_guid.');
                    }
                    return { type, identifier: toText(row.identifier) };
                })
            ),
    ]);

    const fields = new Map(
        campaignRows
            .slice(1)
            .filter(row => toText(row[0]))
            .map(row => {
                const label = toText(row[0]);
                return [campaignFieldKeys.get(label) || label, row[1]];
            })
    );
    const name = toText(fields.get('name'));
    if (!name) throw new Error('Kampanjan nimi puuttuu.');
    const statusText = toText(fields.get('status'));
    if (statusText && !['0', '1'].includes(statusText)) {
        throw new Error('Kampanjan tilan tulee olla 0 (aktiivinen) tai 1 (passivoitu).');
    }

    let settings: Record<string, unknown> = {};
    const settingsJson = toText(fields.get('settings_json'));
    if (settingsJson) {
        const parsed: unknown = JSON.parse(settingsJson);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            throw new Error('settings_json-kentän tulee olla JSON-objekti.');
        }
        settings = parsed as Record<string, unknown>;
    }

    const email = toText(fields.get('csv_email'));
    const weekdaysValue = toText(fields.get('csv_weekdays'));
    const time = toTime(fields.get('csv_time'));
    let csvSchedule: { weekdays: number[]; time: string } | null = null;
    if (weekdaysValue || time) {
        if (!weekdaysValue || !time) {
            throw new Error('Aseta sekä csv_weekdays että csv_time tai jätä molemmat tyhjiksi.');
        }
        const weekdays = parseCampaignWeekdays(weekdaysValue);
        if (
            !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(time)
        ) {
            throw new Error('Vientiaika: käytä HH:mm-muotoa.');
        }
        csvSchedule = { weekdays, time };
    }

    const [bookHeader = [], ...bookData] = bookRows;
    const isbnIndex = bookHeader.findIndex(header => ['ISBN', 'isbn'].includes(toText(header)));
    const titleIndex = bookHeader.findIndex(header => ['Kirjan nimi', 'title'].includes(toText(header)));
    if (isbnIndex < 0 || titleIndex < 0) {
        throw new Error('Kirjat-välilehdellä tulee olla ISBN- ja Kirjan nimi -sarakkeet.');
    }
    const books = bookData
        .filter(row => row.some(value => toText(value) !== ''))
        .map(row => ({
            isbn: toText(row[isbnIndex]),
            title: toText(row[titleIndex]),
        }));
    if (!books.length) throw new Error('Lisää vähintään yksi kirja books-välilehdelle.');
    if (!targets.length) throw new Error('Lisää vähintään yksi käyttäjä crm targets tai nova targets -välilehdelle.');
    if (targets.some(target => !target.identifier)) {
        throw new Error('Kohteen identifier-arvo ei saa olla tyhjä.');
    }

    return {
        name,
        status: statusText === '1' ? 1 : 0,
        start_date: parseDate(fields.get('start_date'), 'start_date'),
        end_date: parseDate(fields.get('end_date'), 'end_date'),
        books,
        settings: {
            ...settings,
            csv_email: email || null,
            csv_schedule: csvSchedule,
        },
        targets,
    };
};
